import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Chip,
  Divider,
} from '@mui/material';
import { CheckCircle, Star } from '@mui/icons-material';
import LandingLayout from './LandingLayout';
import ProgressBar from './ProgressBar';

interface PlanOption {
  id: string;
  name: string;
  price: string;
  originalPrice?: string;
  savings?: string;
  popular?: boolean;
  features: string[];
}

const PlanSelection: React.FC = () => {
  const [selectedPlan, setSelectedPlan] = useState<string>('monthly');
  const navigate = useNavigate();

  const plans: PlanOption[] = [
    {
      id: 'monthly',
      name: 'Monthly Plan',
      price: '$14.99',
      features: [
        'Daily wellness check-ins',
        'Up to 8 caregivers in your circle',
        'Two-way SMS communication',
        'Smart scheduling with vacation mode',
        'Real-time alerts and notifications',
        'Comprehensive dashboard',
        '24/7 support',
      ],
    },
    {
      id: 'annual',
      name: 'Annual Plan',
      price: '$130',
      originalPrice: '$179.88',
      savings: 'Save $49.88',
      popular: true,
      features: [
        'Everything in Monthly Plan',
        '2 months free',
        'Priority support',
        'Advanced analytics',
        'Custom check-in messages',
        'Export reports',
        'API access',
      ],
    },
  ];

  const handlePlanSelection = (planId: string) => {
    setSelectedPlan(planId);
  };

  const handleContinue = () => {
    // Save selected plan to localStorage
    localStorage.setItem('selectedBillingPlan', selectedPlan);
    // Navigate to account creation page
    navigate('/signup/account');
  };

  const selectedPlanData = plans.find(plan => plan.id === selectedPlan);

  return (
    <LandingLayout>
      <Container component="main" maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ width: '100%', maxWidth: 800, mb: 4 }}>
            <ProgressBar
              currentStep={1}
              totalSteps={5}
              stepLabels={['Plan', 'Account', 'Loved One', 'Care Circle', 'Payment']}
            />
          </Box>
          
          <Paper elevation={3} sx={{ p: 6, width: '100%', maxWidth: 900 }}>
            <Typography component="h1" variant="h4" align="center" gutterBottom sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
              Choose Your Plan
            </Typography>
            
            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
              Select the plan that works best for you and your family
            </Typography>

            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 2, fontSize: '1.1rem', fontWeight: 'bold' }}>
                Billing Plan
              </FormLabel>
              <RadioGroup
                value={selectedPlan}
                onChange={(e) => handlePlanSelection(e.target.value)}
                sx={{ mb: 4 }}
              >
                <Grid container spacing={3}>
                  {plans.map((plan) => (
                    <Grid item xs={12} md={6} key={plan.id}>
                      <Card
                        sx={{
                          border: selectedPlan === plan.id ? '2px solid #2E7D32' : '1px solid #e0e0e0',
                          position: 'relative',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: '#2E7D32',
                            boxShadow: 3,
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                        onClick={() => handlePlanSelection(plan.id)}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Radio
                              value={plan.id}
                              sx={{ mr: 1 }}
                            />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1 }}>
                              {plan.name}
                            </Typography>
                            {plan.popular && (
                              <Chip
                                icon={<Star />}
                                label="Most Popular"
                                color="primary"
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                          
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                              {plan.price}
                            </Typography>
                            {plan.originalPrice && (
                              <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                {plan.originalPrice}
                              </Typography>
                            )}
                            {plan.savings && (
                              <Typography variant="body2" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                                {plan.savings}
                              </Typography>
                            )}
                          </Box>
                          
                          <Divider sx={{ my: 2 }} />
                          
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                              What's included:
                            </Typography>
                            {plan.features.map((feature, index) => (
                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <CheckCircle sx={{ color: '#4CAF50', fontSize: 20, mr: 1 }} />
                                <Typography variant="body2">{feature}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </RadioGroup>
            </FormControl>

            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleContinue}
                sx={{
                  backgroundColor: '#2E7D32',
                  '&:hover': { backgroundColor: '#1B5E20' },
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Next
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </LandingLayout>
  );
};

export default PlanSelection;
